import { useNavigate } from '@tanstack/react-router';
import { Button, Flex } from '@nula/core-ui';
import { useViewSearch } from './hooks/use-view-search';
import styles from './space-lens-toggle.module.css';

/**
 * Segmented control for flipping a Space between List and Canvas views.
 *
 * Reads the current `?view` search param via TanStack Router. Clicking the
 * inactive option navigates to the same route with the appropriate `search`
 * object.
 *
 * Uses `navigate({ search, replace: true })` — search-param only, no route
 * param changes needed. The `replace: true` avoids duplicate history entries
 * on rapid List ↔ Canvas toggling.
 */
export function SpaceLensToggle() {
  const navigate = useNavigate();
  const { view } = useViewSearch();

  const isCanvas = view === 'canvas';

  const goTo = (view: 'list' | 'canvas') => {
    if (view === 'canvas' && isCanvas) return;
    if (view === 'list' && !isCanvas) return;
    navigate({
      // TanStack Router's generic search type can't be widened without a route
      // definition — `as unknown as Parameters<typeof navigate>[0]['search']`
      // would be more verbose but equally unsound. The cast is minimal and
      // localised to the search-param flip; runtime shape is safe.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: (view === 'canvas' ? { view: 'canvas' } : {}) as any,
      replace: true,
    });
  };

  return (
    <Flex gap="xs" className={styles.root}>
      <Button
        size="sm"
        variant={isCanvas ? 'default' : 'primary'}
        active={!isCanvas}
        data-testid="lens-toggle-list"
        onClick={() => goTo('list')}
      >
        List
      </Button>
      <Button
        size="sm"
        variant={isCanvas ? 'primary' : 'default'}
        active={isCanvas}
        data-testid="lens-toggle-canvas"
        onClick={() => goTo('canvas')}
      >
        Canvas
      </Button>
    </Flex>
  );
}
